// src/modules/operaciones/inventarios/dashboard/PanelArticulos.js
//
// Pestaña "Artículos" del dashboard de mermas:
//   1. Pareto 80/20 de la pérdida bruta por artículo (barras + % acumulado + clase ABC)
//   2. Top 10 artículos con mayor pérdida bruta
//   3. Top 10 artículos con mayor sobrante
//   4. Dispersión venta vs merma neta (atípicos en rojo)
//
// USO
//   <PanelArticulos analisis={d.analisis} />
//
// Espera analisis.articulos = { lista, pareto, topPerdida, topSobrante, dispersion, atipicos }
// (salida de analizarMermas).
//
// DEFINICIONES
//   pérdida bruta = faltantes de la merma desconocida + merma conocida (negativa)
//   Pareto: la barra es la pérdida en valor absoluto; clase A = hasta el 80 % acumulado,
//           B = hasta el 95 %, C = el resto (la clase de un artículo se decide con el acumulado
//           ANTERIOR a él, así el artículo que cruza el 80 % sigue siendo A).
//   Atípico: |z| > 3 sobre la merma neta de los artículos con merma (lo calcula analizarMermas;
//            incluye atípicos por pérdida y por sobrante).
//
// Nota: la dispersión puede tener ~2.000 puntos; se dibuja sin animación para que sea fluida.

import React, { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { BarChart3 } from "lucide-react";

import ChartCard from "./ChartCard";
import ChartTooltip from "./ChartTooltip";
import { COLORES_SEMANTICOS, fmtCompacto, fmtNumero, fmtPorcentaje, truncar } from "../utils/informesComun";

const TICK = { fontSize: 11, fill: COLORES_SEMANTICOS.texto };
const dominioConAire = [(min) => Math.min(min * 1.25, 0), (max) => Math.max(max * 1.25, 0)];

const COLOR_CLASE = {
  A: COLORES_SEMANTICOS.perdidaOscuro,
  B: COLORES_SEMANTICOS.alerta,
  C: COLORES_SEMANTICOS.neutro,
};

const tituloArticulo = (_, f) => `${f?.item ? `${f.item} · ` : ""}${f?.nombre ?? ""}`;

/* ========================================================================== */
/* 1. Pareto 80/20                                                            */
/* ========================================================================== */

function GraficoPareto({ datos, articulosPara80 }) {
  const mostrarLinea80 = articulosPara80 > 0 && articulosPara80 <= datos.length;

  return (
    <ComposedChart data={datos} margin={{ top: 16, right: 8, left: 8, bottom: 8 }}>
      <CartesianGrid vertical={false} stroke={COLORES_SEMANTICOS.grilla} />
      <XAxis
        dataKey="rank"
        tick={TICK}
        tickLine={false}
        minTickGap={8}
        label={{ value: "Artículos (de mayor a menor pérdida)", position: "insideBottom", offset: -2, fontSize: 11, fill: COLORES_SEMANTICOS.texto }}
        height={40}
      />
      <YAxis
        yAxisId="izq"
        tickFormatter={(v) => fmtCompacto(v)}
        tick={TICK}
        width={72}
        tickLine={false}
        axisLine={false}
      />
      <YAxis
        yAxisId="der"
        orientation="right"
        domain={[0, 100]}
        ticks={[0, 20, 40, 60, 80, 100]}
        tickFormatter={(v) => `${v}%`}
        tick={TICK}
        width={44}
        tickLine={false}
        axisLine={false}
      />
      <Tooltip
        cursor={{ fill: "rgba(0,0,0,0.04)" }}
        content={
          <ChartTooltip
            titulo={(_, f) => `#${f?.rank} · ${f?.nombre ?? ""}`}
            formato={{ perdida: "moneda", acumuladoPct: "porcentaje" }}
            filasExtra={(f) => [
              { etiqueta: "Clase ABC", valor: f.clase, color: COLOR_CLASE[f.clase] },
              { etiqueta: "Fabricante", valor: f.fabricante },
            ]}
          />
        }
      />
      <ReferenceLine
        yAxisId="der"
        y={80}
        stroke="#6b7280"
        strokeDasharray="4 4"
        label={{ value: "80 %", position: "insideTopRight", fontSize: 11, fill: "#6b7280" }}
      />
      {mostrarLinea80 && (
        <ReferenceLine
          yAxisId="der"
          x={articulosPara80}
          stroke="#6b7280"
          strokeDasharray="4 4"
          label={{ value: `${fmtNumero(articulosPara80)} art.`, position: "insideTopLeft", fontSize: 11, fill: "#6b7280" }}
        />
      )}
      <Bar yAxisId="izq" dataKey="perdida" name="Pérdida" maxBarSize={28}>
        {datos.map((d) => (
          <Cell key={d.rank} fill={COLOR_CLASE[d.clase] || COLORES_SEMANTICOS.neutro} />
        ))}
      </Bar>
      <Line
        yAxisId="der"
        type="monotone"
        dataKey="acumuladoPct"
        name="% acumulado"
        stroke={COLORES_SEMANTICOS.venta}
        strokeWidth={2}
        dot={false}
      />
    </ComposedChart>
  );
}

function LeyendaClases({ pareto }) {
  const clases = pareto?.clases;
  if (!clases) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
      {["A", "B", "C"].map((c) => (
        <span key={c} className="inline-flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: COLOR_CLASE[c] }} aria-hidden="true" />
          <strong>Clase {c}:</strong> {fmtNumero(clases[c].articulos)} artículos · {fmtPorcentaje(clases[c].pct, 1)} de la pérdida
        </span>
      ))}
    </div>
  );
}

/* ========================================================================== */
/* 2 y 3. Top 10 (barras horizontales)                                        */
/* ========================================================================== */

// Etiqueta única por artículo (dos artículos pueden compartir descripción)
function conEtiqueta(lista) {
  const cuenta = new Map();
  lista.forEach((a) => cuenta.set(a.nombre, (cuenta.get(a.nombre) || 0) + 1));
  return lista.map((a) => ({
    ...a,
    etiqueta: cuenta.get(a.nombre) > 1 && a.item ? `${a.nombre} (${a.item})` : a.nombre,
  }));
}

function BarrasArticulos({ datos, clave, nombreSerie, color }) {
  const etiqueta = (props) => {
    const { x, y, width, height, index } = props;
    const d = datos[index];
    if (!d || x === undefined) return null;
    const izquierda = Math.min(x, x + width);
    const derecha = Math.max(x, x + width);
    const negativo = d[clave] < 0;
    return (
      <text
        x={negativo ? izquierda - 6 : derecha + 6}
        y={y + height / 2}
        textAnchor={negativo ? "end" : "start"}
        dominantBaseline="middle"
        fontSize={11}
        fontWeight={600}
        fill="#374151"
      >
        {fmtCompacto(d[clave])}
      </text>
    );
  };

  return (
    <BarChart data={datos} layout="vertical" margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
      <CartesianGrid horizontal={false} stroke={COLORES_SEMANTICOS.grilla} />
      <XAxis
        type="number"
        domain={dominioConAire}
        tickFormatter={(v) => fmtCompacto(v)}
        tick={TICK}
        tickLine={false}
        axisLine={false}
      />
      <YAxis
        type="category"
        dataKey="etiqueta"
        width={170}
        tick={{ ...TICK, fill: "#374151" }}
        tickFormatter={(v) => truncar(v, 28)}
        tickLine={false}
      />
      <ReferenceLine x={0} stroke="#9ca3af" />
      <Tooltip
        cursor={{ fill: "rgba(0,0,0,0.04)" }}
        content={
          <ChartTooltip
            formato="moneda"
            colorearSigno
            titulo={tituloArticulo}
            filasExtra={(d) => [
              { etiqueta: "Fabricante", valor: d.fabricante },
              { etiqueta: "Venta", valor: d.venta, formato: "moneda", color: COLORES_SEMANTICOS.venta },
              { etiqueta: "Merma neta", valor: d.neta, formato: "moneda" },
            ]}
          />
        }
      />
      <Bar dataKey={clave} name={nombreSerie} fill={color} maxBarSize={24} label={etiqueta} />
    </BarChart>
  );
}

/* ========================================================================== */
/* 4. Dispersión venta vs merma                                               */
/* ========================================================================== */

function GraficoDispersion({ normales, atipicos }) {
  return (
    <ScatterChart margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
      <CartesianGrid stroke={COLORES_SEMANTICOS.grilla} />
      <XAxis
        type="number"
        dataKey="venta"
        name="Venta"
        tickFormatter={(v) => fmtCompacto(v)}
        tick={TICK}
        tickLine={false}
        label={{ value: "Venta ($)", position: "insideBottom", offset: -4, fontSize: 11, fill: COLORES_SEMANTICOS.texto }}
        height={44}
      />
      <YAxis
        type="number"
        dataKey="merma"
        name="Merma neta"
        tickFormatter={(v) => fmtCompacto(v)}
        tick={TICK}
        width={72}
        tickLine={false}
      />
      <ReferenceLine y={0} stroke="#9ca3af" />
      <Tooltip
        cursor={{ strokeDasharray: "3 3" }}
        content={
          <ChartTooltip
            formato="moneda"
            colorearSigno
            titulo={tituloArticulo}
            filasExtra={(f) => [
              { etiqueta: "Fabricante", valor: f.fabricante },
              { etiqueta: "Desviaciones (z)", valor: f.z, formato: "numero" },
            ]}
          />
        }
      />
      <Legend verticalAlign="top" height={28} iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
      <Scatter name="Artículos" data={normales} fill={COLORES_SEMANTICOS.venta} fillOpacity={0.4} isAnimationActive={false} />
      {atipicos.length > 0 && (
        <Scatter name="Atípicos (|z| > 3)" data={atipicos} fill={COLORES_SEMANTICOS.perdida} isAnimationActive={false} />
      )}
    </ScatterChart>
  );
}

/* ========================================================================== */
/* Panel                                                                      */
/* ========================================================================== */

function SinDatos() {
  return (
    <div className="flex flex-col items-center justify-center text-center bg-white border border-dashed border-gray-300 rounded-xl py-16 px-4">
      <BarChart3 className="w-10 h-10 text-gray-300 mb-3" aria-hidden="true" />
      <p className="text-sm font-medium text-gray-700">No hay datos de artículos</p>
      <p className="text-xs text-gray-500 mt-1">Ajusta los filtros o revisa que el archivo tenga artículos con merma.</p>
    </div>
  );
}

const OPCIONES_TOP_PARETO = [15, 30, 50, 100];

export function PanelArticulos({ analisis }) {
  const a = analisis?.articulos;
  const [topPareto, setTopPareto] = useState(30);

  const pareto = a?.pareto;
  const puntos = useMemo(() => pareto?.puntos || [], [pareto]);
  const topPerdida = useMemo(() => conEtiqueta(a?.topPerdida || []), [a]);
  const topSobrante = useMemo(() => conEtiqueta(a?.topSobrante || []), [a]);

  const { normales, atipicos } = useMemo(() => {
    const todos = a?.dispersion || [];
    return { normales: todos.filter((d) => !d.atipico), atipicos: todos.filter((d) => d.atipico) };
  }, [a]);

  if (!a || !analisis?.articulos?.lista?.length) return <SinDatos />;

  // Selector de cuántos artículos mostrar en el Pareto
  const n = Math.min(topPareto, puntos.length);
  const opciones = [...new Set([...OPCIONES_TOP_PARETO.filter((x) => x < puntos.length), n])].sort((x, y) => x - y);
  const datosPareto = puntos.slice(0, n);

  const selectorTop = puntos.length > 1 && (
    <label className="inline-flex items-center gap-1.5 text-xs text-gray-500">
      Mostrar
      <select
        value={n}
        onChange={(e) => setTopPareto(Number(e.target.value))}
        className="rounded-md border border-gray-200 bg-white px-1.5 py-1 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        {opciones.map((v) => (
          <option key={v} value={v}>
            Top {v}
          </option>
        ))}
      </select>
    </label>
  );

  const alturaTop = Math.max(340, Math.max(topPerdida.length, topSobrante.length) * 36 + 60);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <ChartCard
        className="lg:col-span-2"
        titulo="Pareto 80/20 de la pérdida bruta"
        subtitulo={
          pareto && puntos.length > 0
            ? `${fmtNumero(pareto.articulosPara80)} de ${fmtNumero(pareto.articulos)} artículos con pérdida concentran el 80 % de ${fmtCompacto(-pareto.totalPerdida)}`
            : "Pérdida por artículo y porcentaje acumulado"
        }
        vacio={puntos.length === 0}
        mensajeVacio="Ningún artículo tiene pérdida con los filtros actuales."
        acciones={selectorTop || null}
        pie={<LeyendaClases pareto={pareto} />}
        height={380}
      >
        <GraficoPareto datos={datosPareto} articulosPara80={pareto?.articulosPara80 || 0} />
      </ChartCard>

      <ChartCard
        titulo="Top artículos con mayor pérdida"
        subtitulo="Pérdida bruta: faltantes + merma conocida ($)"
        vacio={topPerdida.length === 0}
        mensajeVacio="Ningún artículo tiene pérdida."
        height={alturaTop}
      >
        <BarrasArticulos datos={topPerdida} clave="perdidaBruta" nombreSerie="Pérdida bruta" color={COLORES_SEMANTICOS.perdida} />
      </ChartCard>

      <ChartCard
        titulo="Top artículos con mayor sobrante"
        subtitulo="Sobrantes de la merma desconocida ($)"
        vacio={topSobrante.length === 0}
        mensajeVacio="Ningún artículo tiene sobrantes."
        height={alturaTop}
      >
        <BarrasArticulos datos={topSobrante} clave="sobrantes" nombreSerie="Sobrante" color={COLORES_SEMANTICOS.sobrante} />
      </ChartCard>

      <ChartCard
        className="lg:col-span-2"
        titulo="Venta vs merma neta por artículo"
        subtitulo={`${fmtNumero(normales.length + atipicos.length)} artículos · ${fmtNumero(atipicos.length)} atípicos (|z| > 3) en rojo`}
        vacio={normales.length + atipicos.length === 0}
        height={400}
      >
        <GraficoDispersion normales={normales} atipicos={atipicos} />
      </ChartCard>
    </div>
  );
}

export default PanelArticulos;